import { useEffect, useState } from 'react';
import { api, type EstadoCorreo, type EstadoPedidoMail, type PedidoMail } from '../api';
import { useApi, mensajeDeError, VIVO } from '../lib/useApi';
import {
  Cargando,
  Encabezado,
  EstadoChip,
  Icono,
  Paginador,
  usePaginado,
  PanelError,
  useConfirmar,
  Vacio,
} from '../components/ui';
import {
  aviso,
  botonPrimario,
  botonSecundario,
  c,
  campo,
  encabezadoTabla,
  etiquetaCampo,
  filaTabla,
  tarjeta,
  type Tono,
  HUSO,
} from '../theme';

/**
 * Los pedidos que ZF manda por mail a deposito@.
 *
 * El servidor lee la casilla cada pocos minutos, saca del mail la tabla de
 * artículos y piezas y la fecha de entrega, y deja cada mail como un borrador.
 * Acá una persona lo lee con sus propios ojos, corrige lo que el lector
 * automático haya entendido mal y recién entonces crea la orden de trabajo: el
 * sistema nunca crea una OT sola a partir de un mail.
 */

const cuando = (iso: string) =>
  new Date(iso).toLocaleString('es-AR', {
    timeZone: HUSO,
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

/** 'YYYY-MM-DD' a 'DD/MM/AAAA' sin pasar por Date, que lo corre un día por el huso. */
const fechaDia = (d: string | null) => (d ? d.split('-').reverse().join('/') : '—');

const TONO: Record<EstadoPedidoMail, Tono> = { pendiente: 'warn', creada: 'ok', descartada: 'neu', ilegible: 'err' };
const NOMBRE: Record<EstadoPedidoMail, string> = {
  pendiente: 'Pendiente',
  creada: 'OT creada',
  descartada: 'Descartado',
  ilegible: 'Ilegible',
};

const { display: _d1, alignItems: _a1, gap: _g1, ...celda } = filaTabla;
const { display: _d2, alignItems: _a2, gap: _g2, ...celdaTitulo } = encabezadoTabla;
const titulo = { ...celdaTitulo, textAlign: 'left' } as const;

// ---------------------------------------------------------------- Google (popup)

type ClienteCodigo = { requestCode: () => void };
type GoogleOauth = {
  accounts: {
    oauth2: {
      initCodeClient: (cfg: {
        client_id: string;
        scope: string;
        ux_mode: 'popup';
        callback: (r: { code?: string; error?: string }) => void;
      }) => ClienteCodigo;
    };
  };
};
const googleDeWindow = () => (window as unknown as { google?: GoogleOauth }).google;

/** Carga el script de Google Identity Services una sola vez. */
function cargarGis(): Promise<void> {
  if (googleDeWindow()?.accounts?.oauth2) return Promise.resolve();
  return new Promise((ok, mal) => {
    const previo = document.querySelector<HTMLScriptElement>('script[src="https://accounts.google.com/gsi/client"]');
    const s = previo ?? document.createElement('script');
    s.addEventListener('load', () => ok());
    s.addEventListener('error', () => mal(new Error('No pudimos cargar el servicio de Google.')));
    if (!previo) {
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      document.head.appendChild(s);
    }
  });
}

export default function PedidosMail({
  id,
  onAbrir,
  onVolver,
  onVerOrden,
}: {
  id: string | null;
  onAbrir: (id: string) => void;
  onVolver: () => void;
  onVerOrden: (ot: string) => void;
}) {
  // La casilla se llena sola: se vuelve a mirar cada tanto.
  const correo = useApi(() => api.correo(), [], VIVO.normal);
  const estado = correo.datos;

  if (correo.cargando && !estado) return <Cargando texto="Buscando los pedidos…" />;
  if (correo.error && !estado) return <PanelError mensaje={correo.error} onReintentar={correo.recargar} />;
  if (!estado) return null;

  const abierto = id ? estado.pedidos.find((p) => String(p.id) === id) : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1100 }}>
      <Encabezado bajada="Los pedidos que ZF manda por mail a deposito@rdmlogistica.com.ar. Se leen solos, pero la orden de trabajo la crea una persona después de revisarlos." />
      <Casilla estado={estado} recargar={correo.recargar} />
      {id ? (
        abierto ? (
          <Detalle key={abierto.id} pedido={abierto} recargar={correo.recargar} onVolver={onVolver} onVerOrden={onVerOrden} />
        ) : (
          <Vacio
            icono="mail"
            titulo="No encontramos ese pedido"
            detalle="Puede que ya no exista. Volvé a la lista para ver los que hay."
            accion={
              <button type="button" onClick={onVolver} style={botonSecundario}>
                Volver a la lista
              </button>
            }
          />
        )
      ) : (
        <Lista pedidos={estado.pedidos} conectada={!!estado.conectada} onAbrir={onAbrir} onVerOrden={onVerOrden} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- la casilla

function Casilla({ estado, recargar }: { estado: EstadoCorreo; recargar: () => void }) {
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [resultado, setResultado] = useState('');
  const [confirmar, dialogo] = useConfirmar();

  async function conectar() {
    if (!estado.googleClientId) return;
    setError('');
    setResultado('');
    try {
      await cargarGis();
      const g = googleDeWindow();
      if (!g) throw new Error('No pudimos cargar el servicio de Google.');
      g.accounts.oauth2
        .initCodeClient({
          client_id: estado.googleClientId,
          scope: estado.scope,
          ux_mode: 'popup',
          callback: (r) => {
            if (!r.code) {
              if (r.error && r.error !== 'popup_closed') setError('Google no autorizó la conexión.');
              return;
            }
            setOcupado(true);
            api
              .conectarCorreo(r.code)
              .then(recargar)
              .catch((err) => setError(mensajeDeError(err)))
              .finally(() => setOcupado(false));
          },
        })
        .requestCode();
    } catch (err) {
      setError(mensajeDeError(err));
    }
  }

  async function revisar() {
    setOcupado(true);
    setError('');
    setResultado('');
    try {
      const r = await api.revisarCorreo();
      setResultado(r.nuevos === 0 ? 'No hay mails nuevos.' : `Entraron ${r.nuevos} ${r.nuevos === 1 ? 'mail nuevo' : 'mails nuevos'}.`);
      recargar();
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setOcupado(false);
    }
  }

  async function desconectar() {
    const ok = await confirmar({
      titulo: '¿Desconectar la casilla?',
      detalle: 'Deja de leerse el correo y no entran más pedidos. Los que ya están en la lista quedan.',
      boton: 'Desconectar',
    });
    if (!ok) return;
    setError('');
    try {
      await api.desconectarCorreo();
      recargar();
    } catch (err) {
      setError(mensajeDeError(err));
    }
  }

  const { conectada } = estado;

  return (
    <div style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {error && <PanelError mensaje={error} />}

      {conectada ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Icono nombre="mark_email_read" size={24} color={c.verde} />
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>{conectada.mail}</div>
              <div style={{ fontSize: 12.5, color: c.texto2 }}>
                La conectó {conectada.conectadaPor} el {cuando(conectada.conectadaEn)}
                {estado.ultimaRevision && ` · última revisión ${cuando(estado.ultimaRevision)}`}
              </div>
            </div>
            <button type="button" onClick={() => void revisar()} disabled={ocupado} style={{ ...botonSecundario, opacity: ocupado ? 0.5 : 1 }}>
              <Icono nombre="refresh" size={18} />
              Revisar ahora
            </button>
            {estado.puedeConectar && (
              <button type="button" onClick={() => void desconectar()} style={{ ...botonSecundario, color: c.texto2 }}>
                Desconectar
              </button>
            )}
          </div>
          {resultado && <div style={{ fontSize: 13, color: c.texto2 }}>{resultado}</div>}
          {estado.error && (
            <div role="alert" style={aviso('err')}>
              No pudimos leer la casilla la última vez: {estado.error}
            </div>
          )}
        </>
      ) : (
        <>
          <div style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>La casilla todavía no está conectada</div>
          {!estado.configurado ? (
            <div style={aviso('warn')}>Falta GOOGLE_CLIENT_SECRET en el servidor para conectar la casilla.</div>
          ) : estado.puedeConectar ? (
            <>
              <div style={{ fontSize: 13, color: c.texto2, textWrap: 'pretty' }}>
                Se abre una ventana de Google: al conectar tenés que elegir la cuenta <b>deposito@rdmlogistica.com.ar</b>, no la tuya.
                Sólo se pide permiso para leer.
              </div>
              <div>
                <button type="button" onClick={() => void conectar()} disabled={ocupado} style={{ ...botonPrimario, opacity: ocupado ? 0.5 : 1 }}>
                  <Icono nombre="link" size={19} />
                  Conectar casilla
                </button>
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: c.texto2 }}>Un administrador tiene que conectarla.</div>
          )}
        </>
      )}
      {dialogo}
    </div>
  );
}

// ---------------------------------------------------------------- la lista

function Lista({
  pedidos,
  conectada,
  onAbrir,
  onVerOrden,
}: {
  pedidos: PedidoMail[];
  conectada: boolean;
  onAbrir: (id: string) => void;
  onVerOrden: (ot: string) => void;
}) {
  const [filtro, setFiltro] = useState<'abiertos' | 'todos'>('abiertos');
  const visibles = filtro === 'todos' ? pedidos : pedidos.filter((p) => p.estado === 'pendiente' || p.estado === 'ilegible');
  const pagina = usePaginado(visibles);

  return (
    <div style={{ ...tarjeta, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: '14px 16px', borderBottom: `1px solid ${c.borde}` }}>
        <span style={{ flex: 1, fontSize: 16, fontWeight: 600, color: c.texto }}>Mails recibidos</span>
        {(['abiertos', 'todos'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFiltro(f)}
            aria-pressed={filtro === f}
            style={{ ...botonSecundario, padding: '8px 14px', minHeight: 0, fontWeight: filtro === f ? 700 : 500 }}
          >
            {f === 'abiertos' ? 'Para revisar' : 'Todos'}
          </button>
        ))}
      </div>

      {visibles.length === 0 ? (
        <Vacio
          icono="mail"
          titulo={filtro === 'todos' ? 'Todavía no entró ningún mail' : 'No hay pedidos para revisar'}
          detalle={conectada ? 'Cuando ZF mande uno, aparece acá en unos minutos.' : 'Cuando se conecte la casilla, los pedidos van a aparecer acá.'}
        />
      ) : (
        <div className="wms-scroll-x">
          <table className="wms-tarjetas" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: c.fondo, borderBottom: `1px solid ${c.borde}` }}>
                <th style={{ ...titulo, width: 130 }}>Recibido</th>
                <th style={{ ...titulo, minWidth: 260 }}>Mail</th>
                <th style={{ ...titulo, width: 120 }}>Entrega</th>
                <th style={{ ...titulo, width: 130 }}>Estado</th>
                <th style={{ ...titulo, width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {pagina.visibles.map((p) => (
                <tr key={p.id} onClick={() => onAbrir(String(p.id))} style={{ cursor: 'pointer', borderBottom: `1px solid ${c.borde}` }}>
                  <td data-rotulo="Recibido" style={{ ...celda, fontSize: 12.5, color: c.texto2, fontVariantNumeric: 'tabular-nums' }}>
                    {cuando(p.recibidoEn)}
                  </td>
                  <td className="wms-principal" style={celda}>
                    <span style={{ display: 'grid', gap: 2 }}>
                      <span style={{ fontWeight: 500 }}>{p.asunto || '(sin asunto)'}</span>
                      <span style={{ fontSize: 12.5, color: c.texto2, wordBreak: 'break-all' }}>{p.de}</span>
                    </span>
                  </td>
                  <td data-rotulo="Entrega" style={{ ...celda, fontSize: 12.5, color: c.texto2, fontVariantNumeric: 'tabular-nums' }}>
                    {fechaDia(p.entregaEstimada)}
                  </td>
                  <td data-rotulo="Estado" style={celda}>
                    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                      <EstadoChip estado={NOMBRE[p.estado]} tono={TONO[p.estado]} />
                      {p.faltaStock && (p.estado === 'pendiente' || p.estado === 'ilegible') && (
                        <EstadoChip estado="Falta stock" tono="err" />
                      )}
                    </span>
                  </td>
                  <td className="wms-acciones" style={{ ...celda, textAlign: 'right' }}>
                    {p.ordenId && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onVerOrden(p.ordenId!);
                        }}
                        style={{ ...botonSecundario, padding: '8px 14px', minHeight: 0 }}
                      >
                        {p.ordenId}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Paginador pagina={pagina.pagina} paginas={pagina.paginas} total={pagina.total} desde={pagina.desde} que="mails" onIr={pagina.setPagina} />
    </div>
  );
}

// ---------------------------------------------------------------- el detalle

type Renglon = { articulo: string; piezas: string };

function Detalle({
  pedido,
  recargar,
  onVolver,
  onVerOrden,
}: {
  pedido: PedidoMail;
  recargar: () => void;
  onVolver: () => void;
  onVerOrden: (ot: string) => void;
}) {
  const editable = pedido.estado === 'pendiente' || pedido.estado === 'ilegible';
  const cuerpo = useApi(() => api.correoHtml(pedido.id), [pedido.id]);
  const [renglones, setRenglones] = useState<Renglon[]>(() =>
    pedido.lineas.length > 0
      ? pedido.lineas.map((l) => ({ articulo: l.articulo, piezas: String(l.piezas) }))
      : [{ articulo: '', piezas: '' }],
  );
  const [entrega, setEntrega] = useState(pedido.entregaEstimada ?? '');
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [confirmar, dialogo] = useConfirmar();

  // Si el servidor re-lee el mail y cambian los renglones, no pisamos lo que la persona está tocando.
  useEffect(() => setError(''), [pedido.estado]);

  // Lo que se sabe del artículo viene del renglón que leyó el servidor: si la persona
  // cambia el código a otro que el servidor no vio, no hay descripción hasta que se cree.
  const conocido = (codigo: string) => pedido.lineas.find((l) => l.articulo === codigo.trim());

  const validos = renglones.filter((r) => r.articulo.trim() && Number(r.piezas) >= 1);
  const listo = validos.length === renglones.length && renglones.length > 0;

  function editar(i: number, cambio: Partial<Renglon>) {
    setRenglones((antes) => antes.map((r, n) => (n === i ? { ...r, ...cambio } : r)));
  }

  async function crear() {
    setOcupado(true);
    setError('');
    try {
      const r = await api.crearOtDesdeMail(pedido.id, {
        lineas: renglones.map((l) => ({ articulo: l.articulo.trim(), piezas: Number(l.piezas) })),
        entregaEstimada: entrega || null,
        ...(observaciones.trim() ? { observaciones: observaciones.trim() } : {}),
      });
      recargar();
      onVerOrden(r.id);
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setOcupado(false);
    }
  }

  async function releer() {
    setOcupado(true);
    setError('');
    try {
      const l = await api.releerMail(pedido.id);
      // Pisa lo que se había corregido a mano: es lo que pide el botón.
      setRenglones(
        l.lineas.length > 0
          ? l.lineas.map((x) => ({ articulo: x.articulo, piezas: String(x.piezas) }))
          : [{ articulo: '', piezas: '' }],
      );
      setEntrega(l.entrega ?? '');
      recargar();
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setOcupado(false);
    }
  }

  async function descartar() {
    const ok = await confirmar({
      titulo: '¿Descartar este pedido?',
      detalle: 'No se crea ninguna orden de trabajo. El mail queda en la lista como descartado.',
      boton: 'Descartar',
    });
    if (!ok) return;
    setOcupado(true);
    setError('');
    try {
      await api.descartarMail(pedido.id);
      recargar();
      onVolver();
    } catch (err) {
      setError(mensajeDeError(err));
      setOcupado(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button type="button" onClick={onVolver} style={{ ...botonSecundario, padding: '8px 14px', minHeight: 0 }}>
          <Icono nombre="arrow_back" size={18} />
          Volver
        </button>
        <EstadoChip estado={NOMBRE[pedido.estado]} tono={TONO[pedido.estado]} />
        {pedido.ordenId && (
          <button type="button" onClick={() => onVerOrden(pedido.ordenId!)} style={{ ...botonSecundario, padding: '8px 14px', minHeight: 0 }}>
            Ver {pedido.ordenId}
          </button>
        )}
      </div>

      <div style={{ ...tarjeta, padding: 20, display: 'grid', gap: 6 }}>
        <div style={{ fontSize: 17, fontWeight: 600, color: c.texto }}>{pedido.asunto || '(sin asunto)'}</div>
        <div style={{ fontSize: 13, color: c.texto2, wordBreak: 'break-all' }}>
          {pedido.de} · {cuando(pedido.recibidoEn)}
        </div>
        {pedido.resueltoPor && pedido.resueltoEn && (
          <div style={{ fontSize: 12.5, color: c.texto3 }}>
            Lo resolvió {pedido.resueltoPor} el {cuando(pedido.resueltoEn)}
          </div>
        )}
        {pedido.error && <div style={{ ...aviso(pedido.estado === 'ilegible' ? 'err' : 'warn'), marginTop: 6 }}>{pedido.error}</div>}
      </div>

      <div style={{ ...tarjeta, overflow: 'hidden' }}>
        {cuerpo.cargando && <Cargando texto="Abriendo el mail…" />}
        {cuerpo.error && <PanelError mensaje={cuerpo.error} onReintentar={cuerpo.recargar} />}
        {cuerpo.datos && (
          // Sin scripts (sandbox vacío) y sobre blanco: el mail viene con sus propios colores.
          <iframe
            title="Cuerpo del mail"
            sandbox=""
            srcDoc={cuerpo.datos.html}
            style={{ width: '100%', height: 420, border: 'none', background: '#fff', display: 'block' }}
          />
        )}
      </div>

      {editable && (
        <div style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: c.texto }}>Qué se pide</div>
          <div style={{ fontSize: 13, color: c.texto2, textWrap: 'pretty' }}>
            Revisá que los códigos y las piezas coincidan con el mail. Lo que corrijas es lo que se carga en la orden de trabajo.
          </div>
          {pedido.faltaStock && (
            <div style={aviso('err')}>
              No hay stock para todo lo que se pide. Decidí si se crea la OT igual (sale lo que haya y el resto queda
              como faltante), si se corrigen las piezas o si se descarta y se le avisa a ZF.
            </div>
          )}

          {renglones.map((r, i) => {
            const k = conocido(r.articulo);
            const piezas = Number(r.piezas);
            return (
              <div key={i} style={{ display: 'grid', gap: 6, padding: 12, border: `1px solid ${c.borde}`, borderRadius: 16 }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <label style={{ ...etiquetaCampo, flex: 2, minWidth: 180 }}>
                    Artículo
                    <input value={r.articulo} onChange={(e) => editar(i, { articulo: e.target.value })} style={campo} />
                  </label>
                  <label style={{ ...etiquetaCampo, flex: 1, minWidth: 120 }}>
                    Piezas
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      value={r.piezas}
                      onChange={(e) => editar(i, { piezas: e.target.value })}
                      style={campo}
                    />
                  </label>
                  {renglones.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setRenglones((antes) => antes.filter((_, n) => n !== i))}
                      title="Quitar este renglón"
                      style={{ ...botonSecundario, padding: '8px 12px', minHeight: 0 }}
                    >
                      <Icono nombre="close" size={17} color={c.texto3} />
                    </button>
                  )}
                </div>
                {k && (
                  <div style={{ fontSize: 12.5, color: k.existe ? c.texto2 : c.rojo }}>
                    {k.existe ? (k.descripcion ?? 'Sin descripción') : 'No está en el maestro'}
                    {k.existe && k.maxPorCapacho && piezas >= 1 && (() => {
                      const n = Math.ceil(piezas / k.maxPorCapacho);
                      return ` · salen ${n} ${n === 1 ? 'capacho' : 'capachos'} (hasta ${k.maxPorCapacho} piezas cada uno)`;
                    })()}
                  </div>
                )}
                {k?.existe && (
                  // Se compara contra lo que la persona escribió, no contra lo que decía el mail.
                  <div style={{ fontSize: 12.5, color: piezas > k.disponible ? c.rojo : c.texto2 }}>
                    {piezas > k.disponible ? `Faltan ${piezas - k.disponible} pz: ` : 'Alcanza: '}
                    hay {k.disponible} disponibles
                    {k.comprometidas > 0 && ` (${k.comprometidas} ya pedidas por otras OT)`}
                    {k.enCompletos > 0 && ` · ${k.enCompletos} pz más en pallets completos`}
                  </div>
                )}
              </div>
            );
          })}

          <div>
            <button
              type="button"
              onClick={() => setRenglones((antes) => [...antes, { articulo: '', piezas: '' }])}
              style={{ ...botonSecundario, padding: '9px 14px' }}
            >
              <Icono nombre="add" size={18} color={c.texto3} />
              Agregar otro artículo
            </button>
          </div>

          <label style={{ ...etiquetaCampo, maxWidth: 240 }}>
            Entrega estimada
            <input type="date" value={entrega} onChange={(e) => setEntrega(e.target.value)} style={campo} />
          </label>
          <label style={etiquetaCampo}>
            Observaciones (opcional)
            <input value={observaciones} onChange={(e) => setObservaciones(e.target.value)} maxLength={300} style={campo} />
          </label>

          {error && <PanelError mensaje={error} />}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => void crear()}
              disabled={ocupado || !listo}
              style={{ ...botonPrimario, opacity: ocupado || !listo ? 0.5 : 1 }}
            >
              <Icono nombre="assignment_add" size={19} />
              {ocupado ? 'Creando…' : 'Crear OT'}
            </button>
            <button type="button" onClick={() => void releer()} disabled={ocupado} style={botonSecundario}>
              <Icono nombre="refresh" size={18} color={c.texto3} />
              Volver a leer el mail
            </button>
            <button type="button" onClick={() => void descartar()} disabled={ocupado} style={{ ...botonSecundario, color: c.texto2 }}>
              Descartar
            </button>
          </div>
        </div>
      )}
      {dialogo}
    </div>
  );
}
